import { useId, useState } from "react";
import { checkIsDomElement } from "../helpers/dom.js";

const CUSTOM_MIME = "application/vnd.alpharun.drag-item";

interface UseDraggableListProps<T> {
  items: readonly T[];
  onReorder: (newItems: readonly T[]) => void;
}

interface DragHandlers {
  handleDragStart: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragEnd: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: () => void;
  dragOverIndex: number | null;
}

export function useDraggableList<T>({
  items,
  onReorder,
}: UseDraggableListProps<T>): DragHandlers {
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const listId = useId();

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    const { currentTarget: item, target } = e;

    // Prevent drag from inputs/textareas to avoid breaking selection
    if (
      checkIsDomElement(target) &&
      (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
    ) {
      e.preventDefault();
      return;
    }

    // If there are drag handles, only allow dragging from them
    if (checkContainsDragHandle(item) && !checkIsDragHandle(target)) {
      e.preventDefault();
      return;
    }

    const index = Number(item.dataset.itemIndex);
    e.dataTransfer.setData(CUSTOM_MIME, JSON.stringify({ index, listId }));

    // Use the current target as the drag image even if a drag handle was used
    const itemRect = item.getBoundingClientRect();
    const mouseX = e.clientX - itemRect.left;
    const mouseY = e.clientY - itemRect.top;
    e.dataTransfer.setDragImage(e.currentTarget, mouseX, mouseY);

    e.currentTarget.classList.add("opacity-50");
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const dropIndex = Number(e.currentTarget.dataset.itemIndex);

    // If we're on the bottom half of the item, we'll drop after it
    if (y > rect.height / 2) {
      setDragOverIndex(dropIndex + 1);
    } else {
      setDragOverIndex(dropIndex);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData(CUSTOM_MIME));
    const { index: dragIndex, listId: sourceListId } = data;

    // Only allow drops within the same list
    if (sourceListId !== listId) return;

    if (dragOverIndex === null || dragIndex === dragOverIndex) return;

    const newItems = [...items];
    const [removed] = newItems.splice(Number(dragIndex), 1);
    if (removed === undefined) return;

    // When dropping after the drag position, decrement the drop index by 1 since removing the dragged item shifts all subsequent indices down
    const adjustedDropIndex =
      dragOverIndex > dragIndex ? dragOverIndex - 1 : dragOverIndex;

    newItems.splice(adjustedDropIndex, 0, removed);

    onReorder(newItems);
    setDragOverIndex(null);
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove("opacity-50");
    setDragOverIndex(null);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  return {
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    handleDragLeave,
    dragOverIndex,
  };
}

const dragHandleSelector = '[data-drag-handle="true"]';

function checkIsDragHandle(target: EventTarget) {
  return (
    checkIsDomElement(target) && target.closest(dragHandleSelector) !== null
  );
}

function checkContainsDragHandle(currentTarget: EventTarget & HTMLDivElement) {
  return currentTarget.querySelector(dragHandleSelector) !== null;
}
